/**
 * ============================================================================
 * 统一 API 模块导出
 * ============================================================================
 *
 * 文件职责：
 * - 统一管理所有业务模块的 API 实例
 * - 使用工厂函数模式创建 API 对象
 * - 提供完整的类型定义导出
 *
 * 设计理念：
 * - 集中管理：所有 API 实例在此文件中创建和导出
 * - 依赖注入：统一注入 httpAdapter
 * - 类型安全：导出完整的类型定义
 * - 易于维护：新增模块只需添加一行导入和一行导出
 *
 * 使用方式：
 *
 * ### 1. 在组件中导入使用
 * ```typescript
 * import { materialApi } from '#/api/modules'
 *
 * // 调用 API
 * const materials = await materialApi.getMaterialList({ page: 1 })
 * const material = await materialApi.getMaterialDetail(1)
 * ```
 *
 * ### 2. 在模块 API 文件中重导出
 * ```typescript
 * // modules/mes/material/api/index.ts
 * export { materialApi } from '#/api/modules'
 *
 * // 或者重命名导出
 * export { materialApi as api } from '#/api/modules'
 * ```
 *
 * ### 3. 批量导出多个 API
 * ```typescript
 * export {
 *   materialApi,
 *   bomApi,
 *   warehouseApi,
 *   inventoryApi,
 * } from '#/api/modules'
 * ```
 *
 * ⚠️ 注意事项：
 *
 * ### 1. 工厂函数模式
 * - 所有 API 都通过工厂函数创建
 * - 工厂函数位于 `@bayes-mes/shared/api/{module}/factory.ts`
 * - 需要传入 httpAdapter 作为请求客户端
 *
 * ### 2. 类型安全
 * - 所有 API 实例都有完整的类型定义
 * - 支持 IDE 自动补全和类型检查
 * - 返回值类型自动推断
 *
 * ### 3. 维护方便
 * - 新增模块：添加 import 和 export 两行
 * - 删除模块：删除对应的 import 和 export
 * - 所有使用 API 的代码无需修改
 *
 * 更新流程：
 *
 * 当 shared 库新增模块时：
 * ```typescript
 * // 1. 导入工厂函数
 * import { createNewModuleApi } from '@bayes-mes/shared/api/new-module/factory'
 *
 * // 2. 创建 API 实例
 * export const newModuleApi = createNewModuleApi(httpAdapter)
 *
 * // 3. 导出类型（可选）
 * export type { NewModuleApi } from '@bayes-mes/shared'
 * ```
 *
 * @module api/modules
 * @description 统一 API 模块导出
 *
 * @author AI Assistant
 * @created 2025-10-19
 * @last-updated 2025-10-19
 *
 * @change-log
 * - 2025-10-19: 初始创建，集成所有已完成模块的工厂函数
 *
 * @notes
 * - 基于 shared 库 v3.10.0
 * - 包含 20 个已完成模块
 * - 使用工厂函数模式实现依赖注入
 * ============================================================================
 */

// App 监控日志模块
import { createAppLogsApi } from '@bayes-mes/shared/api/app-logs/factory';
import { createAuditLogApi } from '@bayes-mes/shared/api/audit-log/factory';
// ============================================================================
// 导入所有模块的工厂函数
// ============================================================================
// 核心系统模块
import { createAuthApi } from '@bayes-mes/shared/api/auth/factory';
import { createBomApi } from '@bayes-mes/shared/api/bom/factory';
import { createCustomerMaterialSupplierApi } from '@bayes-mes/shared/api/customer-material-supplier/factory';
// 销售管理模块
import { createCustomerApi } from '@bayes-mes/shared/api/customer/factory';
import { createDepartmentApi } from '@bayes-mes/shared/api/department/factory';
import { createDingTalkApi } from '@bayes-mes/shared/api/dingtalk/factory';
import { createEmployeeApi } from '@bayes-mes/shared/api/employee/factory';
import { createEquipmentApi } from '@bayes-mes/shared/api/equipment/factory';
import { createErrorLogApi } from '@bayes-mes/shared/api/error-log/factory';
// 组织管理模块
import { createFactoryApi } from '@bayes-mes/shared/api/factory/factory';
import { createTeamApi } from '@bayes-mes/shared/api/team/factory';
import { createFeedbackApi } from '@bayes-mes/shared/api/feedback/factory';
// 文件管理模块
import { createFileApi } from '@bayes-mes/shared/api/file/factory';
import { createInventoryApi } from '@bayes-mes/shared/api/inventory/factory';
import { createIoTApi } from '@bayes-mes/shared/api/iot/factory';
// 物流管理模块
import {
  createLogisticsOrderApi,
  createLogisticsProviderApi,
  createLogisticsSettlementApi,
} from '@bayes-mes/shared/api/logistics';
import { createDailyReportApi } from '@bayes-mes/shared/api/daily-report/factory';
import { createManagementApi } from '@bayes-mes/shared/api/management/factory';
import { createMaterialBorrowApi } from '@bayes-mes/shared/api/material-borrow/factory';
// 物料与生产模块
import { createMaterialApi } from '@bayes-mes/shared/api/material/factory';
import { createMenuApi } from '@bayes-mes/shared/api/menu/factory';
import { createNotificationApi } from '@bayes-mes/shared/api/notification/factory';
// OEE（设备综合效率）模块
import { createOeeApi } from '@bayes-mes/shared/api/oee/factory';
// 工序管理模块
import { createOperationApi } from '@bayes-mes/shared/api/operation/factory';
import { createPermissionApi } from '@bayes-mes/shared/api/permission/factory';
// 计划管理模块（MRP运算、齐套分析）
import { createPlanningApi } from '@bayes-mes/shared/api/planning/factory';
import { createPricingApi } from '@bayes-mes/shared/api/pricing/factory';
import { createPrinterApi } from '@bayes-mes/shared/api/printer/factory';
import { createProcessOperationApi } from '@bayes-mes/shared/api/process-operation/factory';
import { createProcessRouteTemplateApi } from '@bayes-mes/shared/api/process-route-template/factory';
import { createProcessRouteApi } from '@bayes-mes/shared/api/process-route/factory';
import { createProductionLineApi } from '@bayes-mes/shared/api/production-line/factory';
import { createProductionApi } from '@bayes-mes/shared/api/production/factory';
// 质量管理模块
import {
  createLpaApi,
  createQualityApi,
} from '@bayes-mes/shared/api/quality/factory';
import { createCapaApi } from '@bayes-mes/shared/api/quality/capa-factory';
import { createQrqcApi } from '@bayes-mes/shared/api/quality/qrqc-factory';
import { createRoleApi } from '@bayes-mes/shared/api/role/factory';
import { createSalesOrderApi } from '@bayes-mes/shared/api/sales-order/factory';
import { createShipmentApi } from '@bayes-mes/shared/api/shipment/factory';
import { createSystemConfigApi } from '@bayes-mes/shared/api/system-config/factory';
import { createSystemLogApi } from '@bayes-mes/shared/api/system-log/factory';
import { createToolingApi } from '@bayes-mes/shared/api/tooling/factory';
import { createTraceabilityApi } from '@bayes-mes/shared/api/traceability/factory';
import { createTrainingApi } from '@bayes-mes/shared/api/training/factory';
import { createUnitConversionApi } from '@bayes-mes/shared/api/unit-conversion/factory';
import { createUserApi } from '@bayes-mes/shared/api/user/factory';
import { createWarehouseApi } from '@bayes-mes/shared/api/warehouse/factory';
import { createWebLogsApi } from '@bayes-mes/shared/api/web-logs/factory';
// 组织架构子模块
import { createWorkshopApi } from '@bayes-mes/shared/api/workshop/factory';
import { createWorkstationApi } from '@bayes-mes/shared/api/workstation/factory';

// ============================================================================
// 导入 HTTP 适配器
// ============================================================================
import { batchHttpAdapter, httpAdapter } from '../adapter/http';

// ============================================================================
// 创建 API 实例（注入 httpAdapter）
// ============================================================================

/**
 * 认证授权 API
 * @description 用户登录、登出、Token 刷新等
 */
export const authApi = createAuthApi(httpAdapter);

/**
 * 用户管理 API
 * @description 用户的增删改查、密码修改、状态管理等
 */
export const userApi = createUserApi(httpAdapter);

/**
 * 权限管理 API
 * @description 权限的增删改查、权限分配等
 */
export const permissionApi = createPermissionApi(httpAdapter);

/**
 * 角色管理 API
 * @description 角色的增删改查、角色权限分配等
 */
export const roleApi = createRoleApi(httpAdapter);

/**
 * 菜单管理 API
 * @description 菜单的增删改查、菜单树结构等
 */
export const menuApi = createMenuApi(httpAdapter);

/**
 * 审计日志 API
 * @description 审计日志查询、统计等
 */
export const auditLogApi = createAuditLogApi(httpAdapter);

/**
 * 错误日志 API
 * @description 错误日志查询、统计、趋势分析等
 */
export const errorLogApi = createErrorLogApi(httpAdapter);

/**
 * 系统日志 API
 * @description 系统日志查询、统计等
 */
export const systemLogApi = createSystemLogApi(httpAdapter);

/**
 * 意见反馈 API
 * @description 意见反馈、工资异议登记、反馈回复与状态跟进
 */
export const feedbackApi = createFeedbackApi(httpAdapter);

/**
 * 系统配置 API
 * @description 系统配置的增删改查、分组管理等
 */
export const systemConfigApi = createSystemConfigApi(httpAdapter);

/**
 * 部门管理 API
 * @description 部门的增删改查、部门树结构等
 */
export const departmentApi = createDepartmentApi(httpAdapter);

/**
 * 钉钉集成 API
 * @description H5 免登配置和组织架构同步
 */
export const dingtalkApi = createDingTalkApi(httpAdapter);

/**
 * 员工管理 API
 * @description 员工的增删改查、员工分配等
 */
export const employeeApi = createEmployeeApi(httpAdapter);

/**
 * 工厂管理 API
 * @description 工厂的增删改查、工厂统计等
 */
export const factoryApi = createFactoryApi(httpAdapter);
export const teamApi = createTeamApi(httpAdapter);

/**
 * 车间管理 API
 * @description 车间的增删改查、车间统计等
 */
export const workshopApi = createWorkshopApi(httpAdapter);

/**
 * 生产线管理 API
 * @description 生产线的增删改查、生产线统计、产能计算等
 */
export const productionLineApi = createProductionLineApi(httpAdapter);

/**
 * 工位管理 API
 * @description 工位的增删改查、工位统计、操作员分配、二维码生成等
 */
export const workstationApi = createWorkstationApi(httpAdapter);

/**
 * 物料管理 API
 * @description 物料的增删改查、物料单位管理、单位转换等
 */
export const materialApi = createMaterialApi(httpAdapter);

/**
 * 物料借用 API
 * @description 物料借用和归还相关的操作
 */
export const materialBorrowApi = createMaterialBorrowApi(httpAdapter);

/**
 * 仓库管理 API
 * @description 仓库的增删改查、库位管理等
 */
export const warehouseApi = createWarehouseApi(httpAdapter);

/**
 * 库存管理 API
 * @description 库存查询、出入库操作、库存调整等
 */
export const inventoryApi = createInventoryApi(httpAdapter);

/**
 * BOM 管理 API
 * @description BOM 的增删改查、BOM 项管理、替代料管理等
 */
export const bomApi = createBomApi(httpAdapter);

/**
 * 工艺路线 API
 * @description 工艺路线的增删改查、工序管理等
 */
export const processRouteApi = createProcessRouteApi(httpAdapter);

/**
 * 生产执行 API
 * @description 覆盖生产订单、工单、报工、投料、报废、返工、批次追溯等子模块
 */
export const productionApi = createProductionApi(httpAdapter);

/**
 * 班长日报 API
 * @description 班组长日报的填报、管理与统计分析
 */
export const dailyReportApi = createDailyReportApi(httpAdapter);

/**
 * 管理驾驶舱 API
 * @description 管理日报/月报统一聚合数据
 */
export const managementApi = createManagementApi(httpAdapter);

/**
 * 工序 API
 * @description 工序的增删改查、排序、流转等
 */
export const processOperationApi = createProcessOperationApi(httpAdapter);

/**
 * 工艺路线模板 API
 * @description 工艺路线模板的增删改查、基于模板生成工艺路线等
 */
export const processRouteTemplateApi =
  createProcessRouteTemplateApi(httpAdapter);

/**
 * 单位换算 API
 * @description 单位换算规则的管理、单位转换计算等
 */
export const unitConversionApi = createUnitConversionApi(httpAdapter);

/**
 * 客户管理 API
 * @description 客户的增删改查、客户统计等
 */
export const customerApi = createCustomerApi(httpAdapter);

/**
 * 客户物料指定供应商 API
 * @description 客户为物料指定供应商的管理、采购校验等
 */
export const customerMaterialSupplierApi =
  createCustomerMaterialSupplierApi(httpAdapter);

/**
 * 销售订单 API
 * @description 销售订单的增删改查、订单项管理、订单状态管理等
 */
export const salesOrderApi = createSalesOrderApi(httpAdapter);

/**
 * 消息通知 API
 * @description 消息通知的增删改查、消息设置管理、消息模板管理等
 */
export const notificationApi = createNotificationApi(httpAdapter);

/**
 * 工装管理 API
 * @description 工装（刀具、检具、夹具、模具）的增删改查、使用管理、维护管理等
 */
export const toolingApi = createToolingApi(httpAdapter);

/**
 * 设备管理 API
 * @description 设备的增删改查、工单绑定、扫码管理等
 */
export const equipmentApi = createEquipmentApi(httpAdapter);

/**
 * 打印机管理 API
 * @description 打印机的增删改查、状态管理等
 */
export const printerApi = createPrinterApi(httpAdapter);

/**
 * 产品工价管理 API
 * @description 产品计价配置、工价维护、工资记录、多维度工资统计
 */
export const pricingApi = createPricingApi(httpAdapter);

/**
 * 溯源系统 API
 * @description 正向追溯（原材料→成品）、反向追溯（成品→原材料）、全链路追溯等
 */
export const traceabilityApi = createTraceabilityApi(httpAdapter);

/**
 * 物联网（IoT）管理 API
 * @description 厂家管理、设备管理、实时监控、报警管理、统计分析等
 */
export const iotApi = createIoTApi(httpAdapter);

/**
 * OEE（设备综合效率）管理 API
 * @description OEE看板、配置管理、停机记录等
 */
export const oeeApi = createOeeApi(httpAdapter);

/**
 * 质量管理 API
 * @description 检验项目、检验模板、检验任务、NCR、AQL、自检配置、IQC、质量看板等
 */
export const qualityApi = createQualityApi(httpAdapter);

// ============================================================================
// 批量导入专用 API 实例（batchHttpAdapter：2 分钟超时）
//
// 数据导入是后端长事务操作，走默认 15 秒超时会前端报错、后端仍在执行，
// 用户重试会造成并发导入堆积。各导入入口一律使用对应的 *BatchApi。
// ============================================================================
export const materialBatchApi = createMaterialApi(batchHttpAdapter);
export const processRouteBatchApi = createProcessRouteApi(batchHttpAdapter);
export const qualityBatchApi = createQualityApi(batchHttpAdapter);
export const toolingBatchApi = createToolingApi(batchHttpAdapter);
export const equipmentBatchApi = createEquipmentApi(batchHttpAdapter);
export const inventoryBatchApi = createInventoryApi(batchHttpAdapter);
export const workstationBatchApi = createWorkstationApi(batchHttpAdapter);
export const warehouseBatchApi = createWarehouseApi(batchHttpAdapter);

/**
 * LPA 分层过程审核 API
 * @description 层级配置、审核模板、审核任务、问题项、统计数据等
 */
export const lpaApi = createLpaApi(httpAdapter);
export const capaApi = createCapaApi(httpAdapter);
export const qrqcApi = createQrqcApi(httpAdapter);

/**
 * 计划管理 API
 * @description MRP运算、齐套分析等
 */
export const planningApi = createPlanningApi(httpAdapter);

/**
 * 文件管理 API
 * @description 文件夹管理、文件上传下载、版本管理、权限管理等
 */
export const fileApi = createFileApi(httpAdapter);

/**
 * 培训管理 API
 * @description 培训类型、培训任务、参与者管理、我的培训、培训统计等
 */
export const trainingApi = createTrainingApi(httpAdapter);

/**
 * 工序管理 API
 * @description 全局工序定义的增删改查、状态管理、统计信息等
 */
export const operationApi = createOperationApi(httpAdapter);

/**
 * App 监控日志 API
 * @description App 性能监控、错误日志、用户行为日志的查询和统计
 */
export const appLogsApi = createAppLogsApi(httpAdapter);

/**
 * 物流供应商管理 API
 * @description 物流供应商的增删改查、状态管理、统计信息等
 */
export const logisticsProviderApi = createLogisticsProviderApi(httpAdapter);

/**
 * 物流订单管理 API
 * @description 物流订单的增删改查、状态管理、轨迹跟踪、评价等
 */
export const logisticsOrderApi = createLogisticsOrderApi(httpAdapter);

/**
 * 物流结算管理 API
 * @description 物流结算单的增删改查、账单导入、确认付款等
 */
export const logisticsSettlementApi = createLogisticsSettlementApi(httpAdapter);

/**
 * 发货管理 API
 * @description 发货单创建、确认、批次选择与状态操作
 */
export const shipmentApi = createShipmentApi(httpAdapter);

/**
 * Web 前端监控 API
 * @description Web 性能、错误、行为与接口日志查询
 */
export const webLogsApi = createWebLogsApi(httpAdapter);

// ============================================================================
// 导出类型定义（通过 typeof 推断，不从 shared 库导入）
// ============================================================================

// 注意：这些 API 类型通过 TypeScript 的 typeof 自动推断，
// 不需要从 shared 库导出，因为它们是运行时对象的类型

export type AuthApi = typeof authApi;
export type UserApi = typeof userApi;
export type PermissionApi = typeof permissionApi;
export type RoleApi = typeof roleApi;
export type MenuApi = typeof menuApi;
export type AuditLogApi = typeof auditLogApi;
export type ErrorLogApi = typeof errorLogApi;
export type SystemLogApi = typeof systemLogApi;
export type FeedbackApi = typeof feedbackApi;
export type SystemConfigApi = typeof systemConfigApi;
export type DepartmentApi = typeof departmentApi;
export type DingTalkApi = typeof dingtalkApi;
export type EmployeeApi = typeof employeeApi;
export type FactoryApi = typeof factoryApi;
export type WorkshopApi = typeof workshopApi;
export type ProductionLineApi = typeof productionLineApi;
export type WorkstationApi = typeof workstationApi;
export type MaterialApi = typeof materialApi;
export type MaterialBorrowApi = typeof materialBorrowApi;
export type WarehouseApi = typeof warehouseApi;
export type InventoryApi = typeof inventoryApi;
export type BomApi = typeof bomApi;
export type ProcessRouteApi = typeof processRouteApi;
export type ProcessRouteTemplateApi = typeof processRouteTemplateApi;
export type DailyReportApi = typeof dailyReportApi;
export type ProductionApi = typeof productionApi;
export type ManagementApi = typeof managementApi;
export type UnitConversionApi = typeof unitConversionApi;
export type CustomerApi = typeof customerApi;
export type CustomerMaterialSupplierApi = typeof customerMaterialSupplierApi;
export type SalesOrderApi = typeof salesOrderApi;
export type NotificationApi = typeof notificationApi;
export type ToolingApi = typeof toolingApi;
export type EquipmentApi = typeof equipmentApi;
export type PrinterApi = typeof printerApi;
export type PricingApi = typeof pricingApi;
export type TraceabilityApi = typeof traceabilityApi;
export type IoTApi = typeof iotApi;
export type OeeApi = typeof oeeApi;
export type QualityApi = typeof qualityApi;
export type LpaApi = typeof lpaApi;
export type PlanningApi = typeof planningApi;
export type FileApi = typeof fileApi;
export type TrainingApi = typeof trainingApi;
export type OperationApi = typeof operationApi;
export type AppLogsApi = typeof appLogsApi;
export type LogisticsProviderApi = typeof logisticsProviderApi;
export type ShipmentApi = typeof shipmentApi;
export type WebLogsApi = typeof webLogsApi;
export type LogisticsOrderApi = typeof logisticsOrderApi;
export type LogisticsSettlementApi = typeof logisticsSettlementApi;
